import { useState } from 'react';

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// eslint-disable-next-line no-unused-vars
const STUDY_TYPE_BADGE = {
  rct: 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
  'randomised controlled trial': 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
  'systematic review': 'rgba(0,0,0,0.08) 0px 0px 0px 1px',
};

function PaperMethodCard({ paper }) {
  const { title, study_type, key_methods, sample_info, endpoints } = paper;

  return (
    <div
      className="bg-white rounded-lg p-4 space-y-3"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
    >
      <p className="text-sm font-semibold text-[#171717] truncate" title={title} style={{ letterSpacing: '-0.32px' }}>
        {title || 'Untitled Paper'}
      </p>

      {study_type && (
        <span
          className="inline-block text-xs font-medium px-2 py-0.5 rounded-full text-[#4d4d4d]"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
        >
          {study_type}
        </span>
      )}

      {key_methods && key_methods.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-1.5">Key Methods</p>
          <div className="flex flex-wrap gap-1.5">
            {key_methods.map((method, i) => (
              <span
                key={i}
                className="text-xs text-[#4d4d4d] px-2 py-0.5 rounded-md"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              >
                {method}
              </span>
            ))}
          </div>
        </div>
      )}

      {sample_info && (
        <p className="text-xs text-[#808080]">
          <span className="font-medium text-[#4d4d4d]">Sample: </span>{sample_info}
        </p>
      )}

      {endpoints && endpoints.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-1">Endpoints</p>
          <ul className="space-y-0.5">
            {endpoints.map((ep, i) => (
              <li key={i} className="text-xs text-[#4d4d4d] flex items-start gap-1.5">
                <span className="text-[#808080] mt-0.5 flex-shrink-0">·</span>
                {ep}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function MethodsExtractor({ papers = [], apiCall }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const canExtract = papers.length >= 2 && !loading;

  const handleExtract = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await apiCall();
      setResult(response.data || response);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to extract methods. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Methods Comparison</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Extract and compare methodologies across your workspace papers.
        </p>
      </div>

      <div className="bg-white rounded-lg p-5 space-y-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
        {papers.length < 2 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
            Add at least 2 papers to compare methods.
          </p>
        )}

        <button
          onClick={handleExtract}
          disabled={!canExtract}
          className="w-full px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                     hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <><Spinner />Extracting methods...</>
          ) : (
            <>Extract &amp; Compare Methods ({papers.length} paper{papers.length !== 1 ? 's' : ''})</>
          )}
        </button>

        {error && <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>}
      </div>

      {result && (
        <div className="space-y-5">
          {result.mock_mode && (
            <div className="bg-amber-50 rounded-md px-4 py-2 text-xs text-amber-700 font-medium" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
              MOCK MODE — Add your OPENROUTER_API_KEY to backend/.env for real AI analysis
            </div>
          )}

          {result.comparative_summary && (
            <div className="rounded-lg p-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
              <h3 className="text-sm font-semibold text-[#171717] mb-2" style={{ letterSpacing: '-0.32px' }}>Comparative Summary</h3>
              <p className="text-sm text-[#4d4d4d] leading-relaxed whitespace-pre-line">
                {result.comparative_summary}
              </p>
            </div>
          )}

          {result.papers && result.papers.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-[#171717] mb-3" style={{ letterSpacing: '-0.32px' }}>Per-Paper Methods</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {result.papers.map((paper, idx) => (
                  <PaperMethodCard key={idx} paper={paper} />
                ))}
              </div>
            </div>
          )}

          {result.recommended_approach && (
            <div className="bg-[#171717] rounded-lg p-4 text-white">
              <h3 className="text-xs font-semibold mb-2 text-white/50 uppercase tracking-wide">
                Recommended Approach
              </h3>
              <p className="text-sm leading-relaxed whitespace-pre-line text-white/90">
                {result.recommended_approach}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
